import { useLingui } from '@lingui/react';
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CoverageStatus, type GroupCoverage } from '@/types/coverage';

interface GroupCoverageBadgeProps {
  coverage: GroupCoverage;
  className?: string;
}

/**
 * Compact badge for calendar header showing coverage status
 * Icon-based: checkmark (green) / warning (yellow) / X (red) based on status
 * Tooltip with coverage details on hover
 */
export function GroupCoverageBadge({
  coverage,
  className,
}: GroupCoverageBadgeProps) {
  const { i18n } = useLingui();

  const status = coverage.status || CoverageStatus.SUFFICIENT;
  const childCount = coverage.child_count ?? 0;
  const scheduledStaff = coverage.scheduled_staff ?? 0;
  const requiredStaff = coverage.required_staff ?? 0;
  const coveragePercent = coverage.coverage_percent ?? 0;
  // An organisation that never saved a staffing model has no rule to be measured
  // against, so the badge claims neither a status nor a figure for it (#1509).
  const isNotConfigured = status === CoverageStatus.NOT_CONFIGURED;

  // Get icon and color based on status
  const getStatusIcon = () => {
    switch (status) {
      case CoverageStatus.CRITICAL:
        return <XCircle className="h-4 w-4 text-destructive-on-surface" aria-hidden="true" />;
      case CoverageStatus.WARNING:
        return <AlertTriangle className="h-4 w-4 text-warning-on-surface" aria-hidden="true" />;
      case CoverageStatus.NOT_CONFIGURED:
        return <MinusCircle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />;
      case CoverageStatus.SUFFICIENT:
      default:
        return <CheckCircle2 className="h-4 w-4 text-success-on-surface" aria-hidden="true" />;
    }
  };

  // Get status label for tooltip - must match existing translations in CoverageStatusBadge
  const getStatusLabel = () => {
    switch (status) {
      case CoverageStatus.CRITICAL:
        return i18n._({
          id: 'coverage.status.critical',
          message: 'Critical',
        });
      case CoverageStatus.WARNING:
        return i18n._({
          id: 'coverage.status.warning',
          message: 'Warning',
        });
      case CoverageStatus.NOT_CONFIGURED:
        return i18n._({
          id: 'coverage.status.notConfigured',
          message: 'Not Configured',
        });
      case CoverageStatus.SUFFICIENT:
      default:
        return i18n._({
          id: 'coverage.status.sufficient',
          message: 'Sufficient',
        });
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn('flex items-center cursor-pointer', className)}
            aria-label={getStatusLabel()}
          >
            {getStatusIcon()}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm space-y-1">
            <div className="font-semibold">{getStatusLabel()}</div>
            <div>
              {i18n._({
                id: 'coverage.badge.tooltip.children',
                message: 'Children: {count}',
                values: { count: childCount },
              })}
            </div>
            {/* required_staff and coverage_percent are left at zero for a
                NOT_CONFIGURED group, so showing them would claim a 0/0 requirement
                and 0% coverage against a rule nobody agreed to (#1509). */}
            {!isNotConfigured && (
              <>
                <div>
                  {i18n._({
                    id: 'coverage.badge.tooltip.staff',
                    message: 'Staff: {scheduled}/{required}',
                    values: { scheduled: scheduledStaff, required: requiredStaff },
                  })}
                </div>
                <div>
                  {i18n._({
                    id: 'coverage.badge.tooltip.coverage',
                    message: 'Coverage: {percent}%',
                    values: { percent: Math.round(coveragePercent) },
                  })}
                </div>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
